import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, Camera, X, AlertTriangle, Package, HelpCircle, Search, Eye, ExternalLink, ShieldCheck, Delete, Megaphone, MessageSquare } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import SurveyLandingPage from './SurveyLandingPage';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/hotels-standalone`;
const INCIDENTS_URL = `${process.env.REACT_APP_BACKEND_URL}/api/incidents`;

const CATEGORY_ICONS = {
  damaged_item: Package,
  spill_hazard: AlertTriangle,
  need_assistance: HelpCircle,
  product_missing: Search,
  suspicious_activity: Eye,
};

const CATEGORY_COLORS = {
  damaged_item: 'border-fuchsia-300 bg-fuchsia-50 text-fuchsia-700',
  spill_hazard: 'border-red-400 bg-red-50 text-red-700',
  need_assistance: 'border-blue-400 bg-blue-50 text-blue-700',
  product_missing: 'border-fuchsia-400 bg-fuchsia-50/60 text-fuchsia-800',
  suspicious_activity: 'border-purple-400 bg-purple-50 text-purple-700',
};

const CATEGORY_SELECTED = {
  damaged_item: 'border-fuchsia-500 bg-fuchsia-100 ring-2 ring-fuchsia-400',
  spill_hazard: 'border-red-500 bg-red-100 ring-2 ring-red-400',
  need_assistance: 'border-blue-500 bg-blue-100 ring-2 ring-blue-400',
  product_missing: 'border-fuchsia-500 bg-fuchsia-100 ring-2 ring-fuchsia-400',
  suspicious_activity: 'border-purple-500 bg-purple-100 ring-2 ring-purple-400',
};

/**
 * Trilingual translation helper for public scan page
 * Returns the correct string based on lang code (en, fr, es)
 */
const t3 = (lang, en, fr, es) => {
  if (lang === 'fr') return fr;
  if (lang === 'es') return es;
  return en;
};

// ============ PIN KEYPAD COMPONENT ============
function PinKeypad({ value, onChange, maxLength = 6 }) {
  const keys = ['1','2','3','4','5','6','7','8','9','CLR','0','OK'];
  return (
    <div className="grid grid-cols-3 gap-2 max-w-[280px] mx-auto" data-testid="pin-keypad">
      {keys.map((key) => {
        if (key === 'CLR') {
          return (
            <button key={key} onClick={() => onChange('')}
              className="h-14 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 font-semibold text-lg transition-colors flex items-center justify-center"
              data-testid="pin-key-clear">
              <Delete className="w-5 h-5" />
            </button>
          );
        }
        if (key === 'OK') {
          return (
            <button key={key} disabled={!value}
              onClick={() => {}} 
              className="h-14 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold text-lg transition-colors"
              data-testid="pin-key-ok">
              OK
            </button>
          );
        }
        return (
          <button key={key}
            onClick={() => { if (value.length < maxLength) onChange(value + key); }}
            className="h-14 rounded-xl bg-white border border-gray-200 hover:bg-amber-50 hover:border-amber-300 text-gray-900 font-semibold text-xl transition-colors"
            data-testid={`pin-key-${key}`}>
            {key}
          </button>
        );
      })}
    </div>
  );
}

export default function RetailerAlertSurvey() {
  const { orgId, locationId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const cardId = searchParams.get('card');
  const langParam = searchParams.get('lang');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [surveyData, setSurveyData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  // steps: action_select | categories | photo_prompt | details | submitting | submitted | unavailable
  // resolve steps: resolve_pin | resolve_confirm | resolve_photo | resolve_submitting | resolve_done
  const [step, setStep] = useState('categories');
  const [textComment, setTextComment] = useState('');
  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lang, setLang] = useState(langParam || 'en');
  const [showLandingPage, setShowLandingPage] = useState(false);
  const [languageSettings, setLanguageSettings] = useState(null);
  const [reviewRedirect, setReviewRedirect] = useState(null);
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);

  // Pyramid 3: engine toggle states
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [commsEnabled, setCommsEnabled] = useState(false);

  // Pyramid 2.1 state
  const [activeHazard, setActiveHazard] = useState(null);
  const [pinCode, setPinCode] = useState('');
  const [pinError, setPinError] = useState('');
  const [validatedUser, setValidatedUser] = useState(null);
  const [resolvePhoto, setResolvePhoto] = useState(null);
  const [resolveResult, setResolveResult] = useState(null);
  const resolvePhotoRef = useRef(null);

  useEffect(() => {
    fetchSurveyData();
  }, [orgId, locationId]);

  const fetchSurveyData = async () => {
    try {
      const response = await fetch(`${API_URL}/retailer-alert-survey/${orgId}/${locationId}?card=${cardId || ''}&lang=${lang}`);
      if (!response.ok) throw new Error('Unable to load reporting interface');
      const data = await response.json();
      setSurveyData(data);

      // Handle language settings
      const ls = data.language_settings || {};
      setLanguageSettings(ls);
      const defaultLang = ls.default_lang || 'en';
      if (!langParam) setLang(defaultLang);
      if (!langParam && ls.landing_page_enabled) setShowLandingPage(true);
      if (data.review_redirect) setReviewRedirect(data.review_redirect);

      // Pyramid 2.1: Store active hazard data (do NOT auto-route — show categories first)
      if (data.active_hazard) {
        setActiveHazard(data.active_hazard);
      }

      // Pyramid 3: Determine 4-state routing
      const alertsOn = data.alerts_enabled !== false;
      const commsOn = data.comms_enabled === true;
      setAlertsEnabled(alertsOn);
      setCommsEnabled(commsOn);

      if (!alertsOn && !commsOn) {
        setStep('unavailable');
      } else if (!alertsOn && commsOn) {
        // Comms only — redirect directly to communications view
        const returnPath = window.location.pathname + window.location.search;
        navigate(`/standalone/communications-view/${orgId}?card=${cardId || ''}&return=${encodeURIComponent(returnPath)}`, { replace: true });
        return;
      } else if (alertsOn && commsOn) {
        setStep('action_select');
      }
      // alertsOn && !commsOn → default 'categories' step (already set)
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_SIZE = 800;
          let w = img.width, h = img.height;
          if (w > MAX_SIZE || h > MAX_SIZE) {
            if (w > h) { h = (h / w) * MAX_SIZE; w = MAX_SIZE; }
            else { w = (w / h) * MAX_SIZE; h = MAX_SIZE; }
          }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.6));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await compressImage(file);
    setPhoto({ dataUrl, name: file.name });
  };

  const handleResolvePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await compressImage(file);
    setResolvePhoto({ dataUrl, name: file.name });
  };

  // ============ STANDARD REPORT SUBMISSION ============
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const category = surveyData.categories.find(c => c.id === selectedCategory);
      const payload = {
        card_id: cardId,
        category_id: selectedCategory,
        category_name: category ? (typeof category.name === 'object' ? category.name[lang] || category.name.en : category.name) : selectedCategory,
        text_comment: textComment.trim(),
      };
      if (photo?.dataUrl) payload.photo = photo.dataUrl;
      const response = await fetch(`${API_URL}/retailer-alert-survey/${orgId}/${locationId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Error submitting report');
      }
      if (reviewRedirect?.enabled && reviewRedirect?.url) {
        setShowReviewPrompt(true);
      } else {
        setStep('submitted');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ============ PYRAMID 2.1: CODE VALIDATION ============
  const handleValidateCode = async () => {
    if (!pinCode.trim()) return;
    setPinError('');
    try {
      const res = await fetch(`${INCIDENTS_URL}/validate-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: pinCode, organization_id: orgId }),
      });
      if (!res.ok) {
        setPinError(t3(lang, 'Invalid code — please try again', 'Code invalide — veuillez réessayer', 'Código inválido — intente de nuevo'));
        return;
      }
      const data = await res.json();
      setValidatedUser(data);
      setStep('resolve_confirm');
    } catch {
      setPinError(t3(lang, 'Connection error', 'Erreur de connexion', 'Error de conexión'));
    }
  };

  // ============ PYRAMID 2.1: RESOLVE SUBMISSION ============
  const handleResolveSubmit = async () => {
    if (!resolvePhoto?.dataUrl) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${INCIDENTS_URL}/${activeHazard.incident_id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: pinCode,
          organization_id: orgId,
          photo: resolvePhoto.dataUrl,
        }),
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Error resolving incident');
      }
      const data = await res.json();
      setResolveResult(data);
      setActiveHazard(null); // Reset so hazard button returns to normal red state
      setStep('resolve_done');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ============ LOGO COMPONENT ============
  const LogoDisplay = () => {
    if (!surveyData?.branding?.logo_url) return null;
    const logoUrl = surveyData.branding.logo_url.startsWith('http')
      ? surveyData.branding.logo_url
      : `${process.env.REACT_APP_BACKEND_URL}${surveyData.branding.logo_url}`;
    const sizeMap = { small: 'h-8', medium: 'h-12', large: 'h-16', very_large: 'h-20' };
    const sizeClass = sizeMap[surveyData.branding.logo_size] || 'h-12';
    return (
      <div className="flex justify-center mb-4">
        <img src={logoUrl} alt="Logo" className={`${sizeClass} object-contain`} />
      </div>
    );
  };

  // ============ LANDING PAGE ============
  if (showLandingPage && surveyData) {
    return (
      <SurveyLandingPage
        branding={surveyData.branding}
        languageSettings={languageSettings}
        organizationName={surveyData.organization?.name || 'Store'}
        onSelectLanguage={(selectedLang) => { setLang(selectedLang); setShowLandingPage(false); }}
        apiBase={process.env.REACT_APP_BACKEND_URL}
      />
    );
  }

  // ============ LOADING ============
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-fuchsia-600 to-fuchsia-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl">
          <CardContent className="pt-6 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-fuchsia-500" />
            <span className="ml-3 text-gray-600">{t3(lang, 'Loading...', 'Chargement...', 'Cargando...')}</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============ ERROR ============
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl">
          <CardContent className="pt-6 text-center">
            <p className="text-red-600 text-lg font-medium mb-2">{t3(lang, 'Error', 'Erreur', 'Error')}</p>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ================================================================
  // PYRAMID 2.1: RESOLVE HAZARD FLOW
  // ================================================================

  // Step: Enter PIN Code
  if (step === 'resolve_pin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl" data-testid="resolve-pin-screen">
          <CardContent className="pt-6 pb-6">
            <LogoDisplay />

            {/* Status Confirmation */}
            <div className="text-center mb-5 bg-green-50 border border-green-200 rounded-xl p-4" data-testid="hazard-status-banner">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h1 className="text-base font-bold text-gray-900 mb-0.5">
                {t3(lang, 'Hazard has been reported', 'Risque signalé', 'Riesgo reportado')}
              </h1>
              <p className="text-sm text-green-700 font-medium">
                {t3(lang, 'Staff have been notified', 'Le personnel a été notifié', 'El personal ha sido notificado')}
              </p>
              <div className="mt-2 flex items-center justify-center space-x-2">
                <p className="text-xs text-gray-500">
                  {activeHazard?.location_label || surveyData?.location?.name}
                </p>
                <span className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-0.5 font-medium">
                  {activeHazard?.category_name}
                </span>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center mb-4">
              <div className="flex-1 border-t border-gray-200" />
              <ShieldCheck className="w-4 h-4 mx-3 text-amber-500" />
              <div className="flex-1 border-t border-gray-200" />
            </div>

            <p className="text-center text-sm font-medium text-gray-700 mb-3">
              {t3(lang, 'Enter code to clear hazard', 'Entrez le code pour éliminer le risque', 'Ingrese el código para eliminar el riesgo')}
            </p>

            {/* PIN Display */}
            <div className="flex justify-center mb-4" data-testid="pin-display">
              <div className="flex gap-2">
                {[...Array(6)].map((_, i) => (
                  <div key={i}
                    className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center text-xl font-bold
                      ${i < pinCode.length ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-gray-200 bg-gray-50'}`}>
                    {pinCode[i] || ''}
                  </div>
                ))}
              </div>
            </div>

            {pinError && (
              <p className="text-center text-sm text-red-600 mb-3 font-medium" data-testid="pin-error">{pinError}</p>
            )}

            <PinKeypad value={pinCode} onChange={(v) => { setPinCode(v); setPinError(''); }} />

            <Button
              onClick={handleValidateCode}
              disabled={!pinCode.trim()}
              className="w-full mt-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold h-12 text-base"
              data-testid="validate-code-btn"
            >
              {t3(lang, 'Validate Code', 'Valider le code', 'Validar código')}
            </Button>

            <Button
              onClick={() => { setActiveHazard(null); setStep('categories'); }}
              className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white font-semibold h-11 text-sm"
              data-testid="report-different-issue-btn"
            >
              {t3(lang, 'Request Different Assistance', 'Demander une autre assistance', 'Solicitar asistencia diferente')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: Hazard Cleared Confirmation
  if (step === 'resolve_confirm') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl" data-testid="resolve-confirm-screen">
          <CardContent className="pt-8 pb-6">
            <LogoDisplay />
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-sm text-gray-500 mb-1">
                {validatedUser?.first_name ? `${validatedUser.first_name} ${validatedUser.last_name || ''}`.trim() : ''}
              </p>
              <h1 className="text-xl font-bold text-gray-900">
                {t3(lang, 'Hazard Cleared?', 'Risque éliminé ?', '¿Riesgo eliminado?')}
              </h1>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                onClick={() => { setStep('resolve_pin'); setPinCode(''); setValidatedUser(null); }}
                className="h-16 text-lg font-bold border-2 border-gray-300 hover:bg-gray-100"
                data-testid="hazard-not-cleared-btn"
              >
                {t3(lang, 'NO', 'NON', 'NO')}
              </Button>
              <Button
                onClick={() => setStep('resolve_photo')}
                className="h-16 text-lg font-bold bg-green-600 hover:bg-green-700 text-white"
                data-testid="hazard-cleared-btn"
              >
                {t3(lang, 'YES', 'OUI', 'SÍ')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: Mandatory Photo Capture
  if (step === 'resolve_photo') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl" data-testid="resolve-photo-screen">
          <CardContent className="pt-8 pb-6">
            <LogoDisplay />
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <Camera className="w-8 h-8 text-blue-600" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">
                {t3(lang, 'Photo Required', 'Photo requise', 'Foto requerida')}
              </h1>
              <p className="text-sm text-gray-500">
                {t3(lang, 'Take a photo of the cleared area for verification.', 'Prenez une photo de la zone dégagée pour vérification.', 'Tome una foto del área despejada para verificación.')}
              </p>
            </div>

            {resolvePhoto ? (
              <div className="relative mb-4">
                <img src={resolvePhoto.dataUrl} alt="Cleared area" className="w-full h-48 object-cover rounded-lg" />
                <button
                  onClick={() => setResolvePhoto(null)}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5"
                  data-testid="remove-resolve-photo-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => resolvePhotoRef.current?.click()}
                className="w-full h-40 border-2 border-dashed border-amber-300 rounded-xl flex flex-col items-center justify-center mb-4 hover:border-amber-400 hover:bg-amber-50/50 transition-colors"
                data-testid="capture-resolve-photo-btn"
              >
                <Camera className="w-10 h-10 text-amber-400 mb-2" />
                <span className="text-sm font-medium text-amber-600">
                  {t3(lang, 'Take Photo', 'Prendre une photo', 'Tomar foto')}
                </span>
              </button>
            )}
            <input
              ref={resolvePhotoRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleResolvePhotoCapture}
              className="hidden"
              data-testid="resolve-photo-input"
            />

            <Button
              onClick={handleResolveSubmit}
              disabled={!resolvePhoto || submitting}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white font-semibold h-12 text-base"
              data-testid="submit-resolve-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" />{t3(lang, 'Submitting...', 'Envoi...', 'Enviando...')}</>
              ) : (
                t3(lang, 'Confirm Resolution', 'Confirmer la résolution', 'Confirmar resolución')
              )}
            </Button>

            <Button
              variant="ghost"
              onClick={() => setStep('resolve_confirm')}
              className="w-full mt-2 text-gray-500"
              data-testid="back-to-confirm-btn"
            >
              {t3(lang, 'Back', 'Retour', 'Volver')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: Resolution Complete
  if (step === 'resolve_done') {
    const mins = resolveResult?.response_time_seconds ? Math.round(resolveResult.response_time_seconds / 60) : null;
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl" data-testid="resolve-done-screen">
          <CardContent className="pt-8 pb-8 text-center">
            <LogoDisplay />
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <ShieldCheck className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t3(lang, 'Hazard Cleared', 'Risque éliminé', 'Riesgo eliminado')}
            </h1>
            <p className="text-gray-500 mb-1">{activeHazard?.location_label}</p>
            {resolveResult?.resolved_by_name && (
              <p className="text-sm text-gray-400 mb-1">
                {t3(lang, 'By', 'Par', 'Por')}: {resolveResult.resolved_by_name}
              </p>
            )}
            {mins !== null && (
              <p className="text-sm text-emerald-600 font-medium">
                {t3(lang, `Response time: ${mins} min`, `Temps de réponse: ${mins} min`, `Tiempo de respuesta: ${mins} min`)}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ================================================================
  // STANDARD REPORTING FLOW
  // ================================================================

  // Review redirect prompt
  if (showReviewPrompt) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-fuchsia-600 to-fuchsia-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl" data-testid="review-redirect-prompt">
          <CardContent className="pt-8 pb-8 text-center">
            <LogoDisplay />
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t3(lang, 'Thank you for your report!', 'Merci pour votre signalement!', '¡Gracias por su reporte!')}
            </h1>
            <p className="text-gray-600 mb-6">
              {t3(lang, 'Would you kindly leave us a review on Google?', 'Pourriez-vous nous laisser un avis sur Google?', '¿Podría dejarnos una reseña en Google?')}
            </p>
            <div className="space-y-3">
              <Button
                className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3"
                onClick={() => window.open(reviewRedirect.url, '_blank')}
                data-testid="leave-review-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {t3(lang, 'Leave a Review', 'Laisser un avis', 'Dejar una reseña')}
              </Button>
              <Button
                variant="ghost"
                className="w-full text-gray-500 hover:text-gray-700"
                onClick={() => { setShowReviewPrompt(false); setStep('submitted'); }}
                data-testid="no-thanks-btn"
              >
                {t3(lang, 'No, thanks', 'Non, merci', 'No, gracias')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Submitted confirmation
  if (step === 'submitted') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-fuchsia-600 to-fuchsia-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl" data-testid="retailer-alert-confirmation">
          <CardContent className="pt-8 pb-8 text-center">
            <LogoDisplay />
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t3(lang, 'Thank you', 'Merci', 'Gracias')}
            </h1>
            <p className="text-gray-600">
              {t3(lang, 'Store staff have been notified.', 'Le personnel du magasin a été notifié.', 'El personal de la tienda ha sido notificado.')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const categories = surveyData?.categories || [];
  const storeName = surveyData?.organization?.name || 'Store';

  // ================================================================
  // PYRAMID 3: SERVICE UNAVAILABLE (neither engine active)
  // ================================================================
  if (step === 'unavailable') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-500 to-gray-700 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl" data-testid="service-unavailable">
          <CardContent className="pt-8 pb-8 text-center">
            <LogoDisplay />
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-gray-400" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {t3(lang, 'Service Unavailable', 'Service non disponible', 'Servicio no disponible')}
            </h1>
            <p className="text-gray-500 text-sm">
              {t3(lang, 'This service point is currently inactive.', 'Ce point de service est actuellement inactif.', 'Este punto de servicio está actualmente inactivo.')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ================================================================
  // PYRAMID 3: ACTION SELECT (both engines active)
  // ================================================================
  if (step === 'action_select') {
    const commsViewUrl = `/standalone/communications-view/${orgId}?card=${cardId || ''}&return=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    return (
      <div className="min-h-screen bg-gradient-to-br from-fuchsia-600 to-fuchsia-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl" data-testid="action-select-screen">
          <CardContent className="pt-6 pb-6">
            <LogoDisplay />
            <div className="text-center mb-6">
              <h1 className="text-xl font-bold text-gray-900 mb-1">{storeName}</h1>
              <p className="text-sm text-gray-500">
                {t3(lang, 'How can we help you?', 'Comment pouvons-nous vous aider?', '¿Cómo podemos ayudarle?')}
              </p>
            </div>

            <div className="space-y-3" data-testid="action-select-buttons">
              {/* Report Issue / Request Assistance */}
              <button
                onClick={() => setStep('categories')}
                className="w-full p-5 rounded-xl border-2 border-fuchsia-300 bg-fuchsia-50 text-left transition-all flex items-center space-x-4 hover:bg-fuchsia-100 hover:border-fuchsia-400"
                data-testid="action-alerts-btn"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-fuchsia-100 flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-fuchsia-600" />
                </div>
                <div>
                  <span className="font-semibold text-base text-gray-900 block">
                    {t3(lang, 'Report Issue / Request Assistance', 'Signaler un problème', 'Reportar problema / Solicitar asistencia')}
                  </span>
                  <span className="text-xs text-gray-500">
                    {t3(lang, 'Alert store staff for help', 'Alerter le personnel du magasin', 'Alertar al personal de la tienda')}
                  </span>
                </div>
              </button>

              {/* Communications / Marketing */}
              <button
                onClick={() => navigate(commsViewUrl)}
                className="w-full p-5 rounded-xl border-2 border-teal-300 bg-teal-50 text-left transition-all flex items-center space-x-4 hover:bg-teal-100 hover:border-teal-400"
                data-testid="action-comms-btn"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                  <Megaphone className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <span className="font-semibold text-base text-gray-900 block">
                    {t3(lang, 'Communications & Offers', 'Communications', 'Comunicaciones y ofertas')}
                  </span>
                  <span className="text-xs text-gray-500">
                    {t3(lang, 'View messages and promotions', 'Voir les messages et promotions', 'Ver mensajes y promociones')}
                  </span>
                </div>
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: Category Selection
  if (step === 'categories') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-fuchsia-600 to-fuchsia-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl" data-testid="retailer-alert-categories">
          <CardContent className="pt-6 pb-6">
            <LogoDisplay />
            <div className="text-center mb-6">
              <h1 className="text-xl font-bold text-gray-900 mb-1">{storeName}</h1>
              <p className="text-sm text-gray-500">
                {t3(lang, 'Select a report type', 'Sélectionnez un type de signalement', 'Seleccione un tipo de reporte')}
              </p>
            </div>

            <div className="space-y-3" data-testid="category-list">
              {categories.map((cat) => {
                const IconComp = CATEGORY_ICONS[cat.id] || AlertTriangle;
                const isHazardActive = cat.id === 'spill_hazard' && activeHazard;

                // Active hazard: yellow button with different text
                if (isHazardActive) {
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setStep('resolve_pin')}
                      className="w-full p-4 rounded-xl border-2 border-amber-400 bg-amber-50 text-amber-800 text-left transition-all flex items-center space-x-4 hover:bg-amber-100"
                      data-testid={`category-${cat.id}`}
                    >
                      <div className="flex-shrink-0">
                        <ShieldCheck className="w-6 h-6 text-amber-600" />
                      </div>
                      <div>
                        <span className="font-semibold text-sm block">
                          {t3(lang, 'Reported Already / Press to Clear', 'Déjà signalé / Appuyez pour résoudre', 'Ya reportado / Presione para resolver')}
                        </span>
                      </div>
                    </button>
                  );
                }

                // Normal button
                const isSelected = selectedCategory === cat.id;
                const colorClass = isSelected
                  ? CATEGORY_SELECTED[cat.id] || 'border-fuchsia-500 bg-fuchsia-100 ring-2 ring-fuchsia-400'
                  : cat.id === 'spill_hazard'
                    ? 'border-red-400 bg-red-50 text-red-700'
                    : CATEGORY_COLORS[cat.id] || 'border-gray-200 bg-gray-50 text-gray-700';

                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setSelectedCategory(cat.id);
                      if (cat.id === 'spill_hazard') {
                        setStep('photo_prompt');
                      } else {
                        setStep('details');
                      }
                    }}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center space-x-4 ${colorClass}`}
                    data-testid={`category-${cat.id}`}
                  >
                    <div className="flex-shrink-0">
                      <IconComp className="w-6 h-6" />
                    </div>
                    <span className="font-medium text-sm">
                      {cat.id === 'spill_hazard' && !activeHazard
                        ? (t3(lang, 'Report Spill or Safety Hazard', 'Signaler un déversement ou risque', 'Reportar derrame o riesgo de seguridad'))
                        : (typeof cat.name === 'object' ? (cat.name[lang] || cat.name.en) : cat.name)}
                    </span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: Photo Prompt (for Spill / Safety Hazard)
  if (step === 'photo_prompt') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-fuchsia-600 to-fuchsia-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl" data-testid="spill-photo-prompt">
          <CardContent className="pt-8 pb-6">
            <LogoDisplay />
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Camera className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 mb-2">
                {t3(lang, 'Take a picture?', 'Prendre une photo?', '¿Tomar una foto?')}
              </h1>
              <p className="text-sm text-gray-500 px-4">
                {t3(lang, 'A photo helps us evaluate the situation.', 'Une photo nous aide à évaluer la situation.', 'Una foto nos ayuda a evaluar la situación.')}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={() => setStep('details')}
                className="h-14 bg-gray-500 hover:bg-gray-600 text-white border-0 text-base font-medium"
                data-testid="photo-prompt-skip"
              >
                {t3(lang, 'Skip', 'Passer', 'Omitir')}
              </Button>
              <Button
                className="h-14 bg-green-600 hover:bg-green-700 text-white border-0 text-base font-medium relative overflow-hidden"
                data-testid="photo-prompt-capture"
                onClick={() => document.getElementById('spill-photo-input').click()}
              >
                <Camera className="w-5 h-5 mr-2" />
                {t3(lang, 'Yes', 'Oui', 'Sí')}
              </Button>
              <input
                id="spill-photo-input"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={async (e) => {
                  await handlePhotoCapture(e);
                  setStep('details');
                }}
                className="hidden"
                data-testid="photo-prompt-input"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step: Details (text + photo)
  if (step === 'details') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-fuchsia-600 to-fuchsia-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl" data-testid="retailer-alert-details">
          <CardContent className="pt-6 pb-6">
            <LogoDisplay />
            <div className="text-center mb-4">
              <h1 className="text-lg font-bold text-gray-900 mb-1">
                {(() => {
                  const cat = categories.find(c => c.id === selectedCategory);
                  return cat ? (typeof cat.name === 'object' ? (cat.name[lang] || cat.name.en) : cat.name) : '';
                })()}
              </h1>
              <p className="text-sm text-gray-500">
                {t3(lang, 'Add details (optional)', 'Ajouter des détails (optionnel)', 'Agregar detalles (opcional)')}
              </p>
            </div>

            <div className="space-y-4">
              <Textarea
                placeholder={t3(lang, 'Describe the issue...', 'Décrivez le problème...', 'Describa el problema...')}
                value={textComment}
                onChange={(e) => setTextComment(e.target.value)}
                className="min-h-[100px] border-gray-200 focus:border-fuchsia-400 focus:ring-fuchsia-400"
                data-testid="text-comment-input"
              />

              {photo ? (
                <div className="relative">
                  <img src={photo.dataUrl} alt="Attached" className="w-full h-40 object-cover rounded-lg" />
                  <button
                    onClick={() => setPhoto(null)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5"
                    data-testid="remove-photo-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-fuchsia-400 transition-colors" data-testid="photo-upload-label">
                  <Camera className="w-5 h-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-500">
                    {t3(lang, 'Take or add a photo', 'Prendre ou ajouter une photo', 'Tomar o agregar una foto')}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoCapture}
                    className="hidden"
                    data-testid="photo-input"
                  />
                </label>
              )}
            </div>

            <div className="flex space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setStep(selectedCategory === 'spill_hazard' ? 'photo_prompt' : 'categories')}
                className="flex-1"
                data-testid="back-btn"
              >
                {t3(lang, 'Back', 'Retour', 'Volver')}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                data-testid="submit-report-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" />{t3(lang, 'Sending...', 'Envoi...', 'Enviando...')}</>
                ) : (
                  t3(lang, 'Submit Report', 'Envoyer le rapport', 'Enviar reporte')
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
